import { isSbefScopeGroupKey } from './sbef-results.helpers';
import { guards } from './typeguards.helpers';
import { isLifecycle } from '../models/lifecycles.model';
import type { SbefScopeGroupKey } from '../construction/sbef-custom-groups.data';
import type { Lifecycle } from '../models/lifecycles.model';

/** Query key for the lifecycle slice of a result scope. */
export const SCOPE_QUERY_LIFECYCLES = 'lifecycles';

/** Query key for the SBEF-group slice of a result scope. */
export const SCOPE_QUERY_SBEF_GROUPS = 'sbefGroups';

/** Query key for the conservative-carbon slice of a result scope. */
export const SCOPE_QUERY_CONSERVATIVE = 'conservative';

const SCOPE_QUERY_KEYS = [
  SCOPE_QUERY_LIFECYCLES,
  SCOPE_QUERY_SBEF_GROUPS,
  SCOPE_QUERY_CONSERVATIVE,
] as const;

/** Scope dimensions that can be snapshotted onto a print URL. */
export type ScopeQueryDimensions = {
  lifecycles: Lifecycle[];
  sbefGroups: SbefScopeGroupKey[];
  conservative: boolean;
};

/** Fields present on the URL; omitted keys are left to the persisted scope. */
export type ScopeQueryOverride = Partial<ScopeQueryDimensions>;

const LIST_SEPARATOR = ',';

const toSearchParams = (search: string | URLSearchParams): URLSearchParams => {
  if (search instanceof URLSearchParams) return search;
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
};

const firstStringQueryValue = (value: unknown): string | undefined => {
  if (guards.is.string(value)) return value;
  if (guards.is.array(value) && guards.is.string(value[0])) return value[0];
  return undefined;
};

const parseLifecycleList = (raw: string): Lifecycle[] | undefined => {
  const parsed = raw
    .split(LIST_SEPARATOR)
    .map((item) => item.trim())
    .filter(isLifecycle);
  return parsed.length > 0 ? parsed : undefined;
};

const parseSbefGroupList = (raw: string): SbefScopeGroupKey[] =>
  raw
    .split(LIST_SEPARATOR)
    .map((item) => item.trim())
    .filter(isSbefScopeGroupKey);

const parseConservativeFlag = (raw: string): boolean | undefined => {
  if (raw === '1' || raw.toLowerCase() === 'true') return true;
  if (raw === '0' || raw.toLowerCase() === 'false') return false;
  return undefined;
};

const hasOverrideFields = (override: ScopeQueryOverride): boolean =>
  override.lifecycles !== undefined ||
  override.sbefGroups !== undefined ||
  override.conservative !== undefined;

/**
 * Encode a scope snapshot as query params so a print/PDF session can replay it
 * without localStorage.
 */
export const toScopeQueryParams = (
  scope: Partial<ScopeQueryDimensions>,
): URLSearchParams => {
  const params = new URLSearchParams();
  if (scope.lifecycles !== undefined) {
    params.set(SCOPE_QUERY_LIFECYCLES, scope.lifecycles.join(LIST_SEPARATOR));
  }
  if (scope.sbefGroups !== undefined) {
    params.set(SCOPE_QUERY_SBEF_GROUPS, scope.sbefGroups.join(LIST_SEPARATOR));
  }
  if (scope.conservative !== undefined) {
    params.set(SCOPE_QUERY_CONSERVATIVE, scope.conservative ? '1' : '0');
  }
  return params;
};

/**
 * Same as {@link toScopeQueryParams} as a plain record for axios `params`.
 */
export const toScopeQueryRecord = (
  scope: Partial<ScopeQueryDimensions>,
): Record<string, string> => Object.fromEntries(toScopeQueryParams(scope));

/**
 * Read a scope override from a query string. Each field is applied only when
 * its param exists and parses to a valid value.
 */
export const parseScopeQueryParams = (
  search: string | URLSearchParams,
): ScopeQueryOverride | undefined => {
  const params = toSearchParams(search);
  const override: ScopeQueryOverride = {};

  if (params.has(SCOPE_QUERY_LIFECYCLES)) {
    const lifecycles = parseLifecycleList(
      params.get(SCOPE_QUERY_LIFECYCLES) ?? '',
    );
    if (lifecycles) override.lifecycles = lifecycles;
  }
  if (params.has(SCOPE_QUERY_SBEF_GROUPS)) {
    override.sbefGroups = parseSbefGroupList(
      params.get(SCOPE_QUERY_SBEF_GROUPS) ?? '',
    );
  }
  if (params.has(SCOPE_QUERY_CONSERVATIVE)) {
    const conservative = parseConservativeFlag(
      params.get(SCOPE_QUERY_CONSERVATIVE) ?? '',
    );
    if (conservative !== undefined) override.conservative = conservative;
  }

  return hasOverrideFields(override) ? override : undefined;
};

/**
 * Express `req.query` is often a null-prototype object (`Object.create(null)`),
 * so this only checks non-null object — not a plain-literal constructor.
 */
const isQueryRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Pull known scope keys off an untyped query object (Express `req.query`).
 */
export const parseScopeQueryParamsFromRecord = (
  query: unknown,
): ScopeQueryOverride | undefined => {
  if (!isQueryRecord(query)) return undefined;
  const params = new URLSearchParams();
  for (const key of SCOPE_QUERY_KEYS) {
    const value = firstStringQueryValue(query[key]);
    if (value !== undefined) params.set(key, value);
  }
  return parseScopeQueryParams(params);
};

/**
 * Validated scope query params to copy onto a print URL, or `undefined` when
 * the request had no usable override.
 */
export const scopeSearchParamsFromQuery = (
  query: unknown,
): URLSearchParams | undefined => {
  const override = parseScopeQueryParamsFromRecord(query);
  return override ? toScopeQueryParams(override) : undefined;
};

/**
 * Overlay {@link parseScopeQueryParams} onto a stored scope. Missing params keep
 * the stored value.
 */
export const applyScopeQueryOverride = (
  scope: ScopeQueryDimensions,
  search: string | URLSearchParams,
): ScopeQueryDimensions => {
  const override = parseScopeQueryParams(search);
  if (!override) return scope;
  return {
    lifecycles: override.lifecycles ?? scope.lifecycles,
    sbefGroups:
      override.sbefGroups !== undefined
        ? [...override.sbefGroups]
        : [...scope.sbefGroups],
    conservative: override.conservative ?? scope.conservative,
  };
};

/**
 * Merge a scope snapshot onto an absolute URL or a same-origin path.
 */
export const appendScopeQueryParamsToUrl = (
  url: string,
  scope: Partial<ScopeQueryDimensions>,
): string => {
  const extra = toScopeQueryParams(scope);
  if ([...extra.keys()].length === 0) return url;

  if (url.startsWith('/')) {
    const [path = url, search = ''] = url.split('?');
    const params = new URLSearchParams(search);
    extra.forEach((value, key) => params.set(key, value));
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }

  const parsed = new URL(url);
  extra.forEach((value, key) => parsed.searchParams.set(key, value));
  return parsed.toString();
};
